import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { usePageWelcome } from '../hooks/usePageWelcome';
import WelcomeModal from '../components/WelcomeModal';
import { updateMealPlanDate } from '../utils/notificationManager';
import { getPublicRecipes, getFavoriteRecipes, addToFavorites, removeFromFavorites } from '../utils/recipeService';
import { supabase } from '../utils/supabaseClient';
import { getMealPlan, addToMealPlan, removeFromMealPlan as removeFromMealPlanDB, getWelcomeModalStatus, markWelcomeModalAsSeen } from '../utils/syncService';

const MemberRecipes = () => {
  const navigate = useNavigate();
  const { showWelcome, closeWelcome } = usePageWelcome('recipes');
  const [showAleraWelcome, setShowAleraWelcome] = useState(false);

  // Check for main Alera welcome on first navigation
  useEffect(() => {
    const checkWelcomeModal = async () => {
      try {
        const status = localStorage.getItem('memberStatus');
        const shouldShowOnFirstNav = localStorage.getItem('showWelcomeOnFirstNav') === 'true';
        const userEmail = localStorage.getItem('userEmail');
        
        if (status === 'active' && shouldShowOnFirstNav && userEmail) {
          // Check Supabase for welcome modal status
          const result = await getWelcomeModalStatus(userEmail);
          
          if (result.success && !result.hasSeen) {
            setTimeout(() => {
              setShowAleraWelcome(true);
            }, 300);
          }
          
          localStorage.removeItem('showWelcomeOnFirstNav');
        }
      } catch (e) {}
    };
    
    checkWelcomeModal();
  }, []);

  const closeAleraWelcome = async () => {
    const userEmail = localStorage.getItem('userEmail');
    
    if (userEmail) {
      // Save to Supabase for cross-device sync
      await markWelcomeModalAsSeen(userEmail);
    }
    
    // Also save to localStorage as backup
    try { localStorage.setItem('welcomeModalSeen', 'true'); } catch (e) {}
    setShowAleraWelcome(false);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMealType, setSelectedMealType] = useState('all');
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [showDietaryFilters, setShowDietaryFilters] = useState(false);
  const [mealPlan, setMealPlan] = useState([]);
  const [loadingMealPlan, setLoadingMealPlan] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [externalRecipes, setExternalRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [recipesError, setRecipesError] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [cookMode, setCookMode] = useState(false);
  const [wakeLock, setWakeLock] = useState(null);
  const [noSleepVideo, setNoSleepVideo] = useState(null);

  const getUserEmail = async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) return null;
      const authEmail = data?.user?.email || null;
      if (authEmail) {
        try {
          localStorage.setItem('userEmail', authEmail);
        } catch (e) {
          // ignore
        }
      }
      return authEmail;
    } catch (e) {
      // ignore
    }

    return null;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Prevent body scroll when any modal is open (mobile-friendly)
  useEffect(() => {
    const isAnyModalOpen = showRecipeModal || showMealPlanModal || showShoppingList;
    
    if (isAnyModalOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [showRecipeModal, showMealPlanModal, showShoppingList]);

  // Load meal plan from Supabase on mount
  useEffect(() => {
    const loadMealPlanFromDB = async () => {
      const userEmail = await getUserEmail();
      if (!userEmail) {
        setLoadingMealPlan(false);
        return;
      }

      try {
        const result = await getMealPlan(userEmail);
        if (result.success) {
          setMealPlan(result.data);
          console.log('Loaded meal plan from Supabase:', result.data.length);
        }
      } catch (error) {
        console.error('Error loading meal plan:', error);
      } finally {
        setLoadingMealPlan(false);
      }
    };

    loadMealPlanFromDB();
  }, []);

  // Load favorites from Supabase
  useEffect(() => {
    const loadFavorites = async () => {
      const userEmail = await getUserEmail();
      if (!userEmail) return;

      try {
        const result = await getFavoriteRecipes(userEmail);
        if (result.success) {
          setFavorites(result.data);
          console.log('Loaded favorites from Supabase:', result.data.length);
        }
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    };

    loadFavorites();
  }, []);

  // Load recipes from Supabase
  useEffect(() => {
    const loadRecipes = async () => {
      setLoadingRecipes(true);
      setRecipesError(null);
      
      try {
        const result = await getPublicRecipes();
        
        if (result.success) {
          setExternalRecipes(result.data);
          console.log('Loaded recipes from Supabase:', result.data.length);
        } else {
          console.error('Failed to load recipes:', result.error);
          setRecipesError(result.error);
          setExternalRecipes([]);
        }
      } catch (error) {
        console.error('Error loading recipes:', error);
        setRecipesError(error.message);
        setExternalRecipes([]);
      } finally {
        setLoadingRecipes(false);
      }
    };

    loadRecipes();

    // Listen for custom events when admin adds new recipes
    const handleRecipesUpdated = () => {
      loadRecipes();
    };

    window.addEventListener('recipesUpdated', handleRecipesUpdated);
    
    return () => {
      window.removeEventListener('recipesUpdated', handleRecipesUpdated);
    };
  }, []);

  const sampleRecipes = [
    {
      id: 1,
      title: 'Coconut Flour Pancakes',
      image: '🥞',
      prepTime: '15 mins',
      servings: 4,
      calories: 185,
      difficulty: 'Easy',
      rating: 4.8,
      reviews: 124,
      mealType: 'breakfast',
      dietary: ['gluten-free', 'dairy-free'],
      nutritionHighlights: ['High Protein', 'Low Carb', 'Fiber Rich'],
      ingredients: [
        { name: 'Coconut flour', amount: '1/2 cup', category: 'Baking' },
        { name: 'Eggs', amount: '4 large', category: 'Dairy & Eggs' },
        { name: 'Coconut milk', amount: '1/4 cup', category: 'Pantry' },
        { name: 'Vanilla extract', amount: '1 tsp', category: 'Pantry' },
        { name: 'Baking powder', amount: '1/2 tsp', category: 'Baking' },
        { name: 'Salt', amount: '1/4 tsp', category: 'Pantry' }
      ],
      instructions: [
        'Whisk together coconut flour, baking powder, and salt',
        'In another bowl, beat eggs and add coconut milk and vanilla',
        'Combine wet and dry ingredients until smooth',
        'Cook on medium heat for 2-3 minutes per side'
      ]
    },
    {
      id: 2,
      title: 'Anti-Inflammatory Golden Milk',
      image: '🥛',
      prepTime: '5 mins',
      servings: 1,
      calories: 120,
      difficulty: 'Easy',
      rating: 4.9,
      reviews: 89,
      mealType: 'treats',
      dietary: ['dairy-free', 'anti-inflammatory'],
      nutritionHighlights: ['Anti-Inflammatory', 'Immune Support', 'Antioxidants'],
      ingredients: [
        { name: 'Coconut milk', amount: '1 cup', category: 'Pantry' },
        { name: 'Turmeric powder', amount: '1/2 tsp', category: 'Spices' },
        { name: 'Ginger powder', amount: '1/4 tsp', category: 'Spices' },
        { name: 'Cinnamon', amount: '1/4 tsp', category: 'Spices' },
        { name: 'Black pepper', amount: 'pinch', category: 'Spices' },
        { name: 'Honey', amount: '1 tsp', category: 'Pantry' }
      ],
      instructions: [
        'Heat coconut milk in a small saucepan',
        'Whisk in turmeric, ginger, cinnamon, and black pepper',
        'Simmer for 2-3 minutes',
        'Strain and sweeten with honey to taste'
      ]
    },
    {
      id: 3,
      title: 'Quinoa Buddha Bowl',
      image: '🥗',
      prepTime: '25 mins',
      servings: 2,
      calories: 420,
      difficulty: 'Medium',
      rating: 4.7,
      reviews: 156,
      mealType: 'lunch',
      dietary: ['gluten-free', 'dairy-free'],
      nutritionHighlights: ['Complete Protein', 'High Fiber', 'Nutrient Dense'],
      ingredients: [
        { name: 'Quinoa', amount: '1 cup', category: 'Grains' },
        { name: 'Sweet potato', amount: '1 large', category: 'Produce' },
        { name: 'Kale', amount: '2 cups', category: 'Produce' },
        { name: 'Chickpeas', amount: '1 can', category: 'Pantry' },
        { name: 'Avocado', amount: '1 medium', category: 'Produce' },
        { name: 'Tahini', amount: '2 tbsp', category: 'Pantry' }
      ],
      instructions: [
        'Cook quinoa according to package directions',
        'Roast cubed sweet potato at 400°F for 20 minutes',
        'Massage kale with a bit of olive oil',
        'Assemble bowl with quinoa, vegetables, and tahini dressing'
      ]
    }
  ];

  // Use admin-managed recipes from Supabase, with fallback to sample recipes if database is empty
  const allRecipes = useMemo(() => externalRecipes.length > 0 ? externalRecipes : sampleRecipes, [externalRecipes]);

  // Calculate dynamic counts based on actual recipes
  const mealTypes = useMemo(() => {
    const getCategoryCount = (categoryId) => {
      if (categoryId === 'all') return allRecipes.length;
      return allRecipes.filter(recipe => {
        const mealType = recipe.mealType || recipe.meal_type;
        return mealType === categoryId;
      }).length;
    };

    return [
      { id: 'all', name: 'All Recipes', icon: '🍽️', count: getCategoryCount('all') },
      { id: 'breakfast', name: 'Breakfast', icon: '🌅', count: getCategoryCount('breakfast') },
      { id: 'lunch', name: 'Lunch', icon: '☀️', count: getCategoryCount('lunch') },
      { id: 'dinner', name: 'Dinner', icon: '🌙', count: getCategoryCount('dinner') },
      { id: 'snacks', name: 'Snacks', icon: '🥨', count: getCategoryCount('snacks') },
      { id: 'treats', name: 'Treats', icon: '🍰', count: getCategoryCount('treats') }
    ];
  }, [allRecipes]);

  // Calculate dynamic counts for dietary filters
  const dietaryFilters = useMemo(() => {
    const normalizeTag = (tag) => tag.toLowerCase().replace(/[-\s]/g, '');
    const getDietaryCount = (dietaryId) => {
      const normalizedFilter = normalizeTag(dietaryId);
      return allRecipes.filter(recipe => {
        const dietary = recipe.dietary || recipe.dietary_tags || recipe.allergen_free || [];
        return Array.isArray(dietary) && dietary.some(tag => normalizeTag(tag) === normalizedFilter);
      }).length;
    };

    return [
      { id: 'gluten-free', name: 'Gluten-Free', count: getDietaryCount('gluten-free') },
      { id: 'dairy-free', name: 'Dairy-Free', count: getDietaryCount('dairy-free') },
      { id: 'egg-free', name: 'Egg-Free', count: getDietaryCount('egg-free') },
      { id: 'soy-free', name: 'Soy-Free', count: getDietaryCount('soy-free') },
      { id: 'peanut-free', name: 'Peanut-Free', count: getDietaryCount('peanut-free') },
      { id: 'tree-nut-free', name: 'Tree Nut-Free', count: getDietaryCount('tree-nut-free') },
      { id: 'pea-free', name: 'Pea-Free', count: getDietaryCount('pea-free') },
      { id: 'sesame-free', name: 'Sesame-Free', count: getDietaryCount('sesame-free') },
      { id: 'shellfish-free', name: 'Shellfish-Free', count: getDietaryCount('shellfish-free') },
      { id: 'fish-free', name: 'Fish-Free', count: getDietaryCount('fish-free') },
      { id: 'sugar-free', name: 'Sugar-Free', count: getDietaryCount('sugar-free') },
      { id: 'vegan', name: 'Vegan', count: getDietaryCount('vegan') },
      { id: 'vegetarian', name: 'Vegetarian', count: getDietaryCount('vegetarian') },
      { id: 'anti-inflammatory', name: 'Anti-Inflammatory', count: getDietaryCount('anti-inflammatory') }
    ];
  }, [allRecipes]);
  
  const filteredRecipes = useMemo(() => {
    const normalizeTag = (tag) => tag.toLowerCase().replace(/[-\s]/g, '');
    const lowerSearch = searchQuery.toLowerCase();

    return allRecipes.filter(recipe => {
      // Support both snake_case (from DB) and camelCase
      const mealType = recipe.mealType || recipe.meal_type;
      const dietary = recipe.dietary || recipe.dietary_tags || recipe.allergen_free || [];
      const recipeName = (recipe.name || recipe.title || '').toLowerCase();
      const recipeIngredients = (recipe.ingredients || []).join(' ').toLowerCase();
      
      // Search filter - check name and ingredients
      const matchesSearch = searchQuery === '' || 
        recipeName.includes(lowerSearch) ||
        recipeIngredients.includes(lowerSearch);
      
      const matchesMealType = selectedMealType === 'all' || mealType === selectedMealType;
      
      // Multi-select dietary filter: recipe must match ALL selected filters
      const matchesDietary = selectedDietary.length === 0 || 
        selectedDietary.every(filter => {
          const normalizedFilter = normalizeTag(filter);
          return Array.isArray(dietary) && dietary.some(tag => 
            normalizeTag(tag) === normalizedFilter
          );
        });
      
      return matchesSearch && matchesMealType && matchesDietary;
    });
  }, [allRecipes, searchQuery, selectedMealType, selectedDietary]);

  const toggleMealPlan = async (recipe) => {
    const userEmail = await getUserEmail();
    if (!userEmail) {
      console.warn('No user email found');
      return;
    }

    if (mealPlan.find(item => item.id === recipe.id)) {
      // Remove from meal plan
      setMealPlan(mealPlan.filter(item => item.id !== recipe.id));
      await removeFromMealPlanDB(userEmail, recipe.id);
    } else {
      // Add to meal plan
      setMealPlan([...mealPlan, recipe]);
      await addToMealPlan(userEmail, recipe);
      // Update meal plan timestamp for notifications
      updateMealPlanDate();
    }
  };

  const removeFromMealPlanLocal = async (recipeId) => {
    const userEmail = await getUserEmail();
    setMealPlan(mealPlan.filter(item => item.id !== recipeId));
    if (userEmail) {
      await removeFromMealPlanDB(userEmail, recipeId);
    }
  };

  const toggleFavorite = async (recipe) => {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData?.user) {
      alert('Please log in to save favorites.');
      navigate('/login', { state: { reason: 'login_required' } });
      return;
    }

    const userEmail = await getUserEmail();
    if (!userEmail) {
      alert('Please log in to save favorites.');
      navigate('/login', { state: { reason: 'login_required' } });
      return;
    }

    const isFav = favorites.find(fav => fav.id === recipe.id);
    
    // Optimistic update
    setFavorites(prev => 
      isFav
        ? prev.filter(fav => fav.id !== recipe.id)
        : [...prev, recipe]
    );

    try {
      if (isFav) {
        const result = await removeFromFavorites(userEmail, recipe.id);
        if (!result.success) {
          // Revert on error
          setFavorites(prev => [...prev, recipe]);
          console.error('Failed to remove favorite:', result.error);
          alert(`Couldn't remove from favorites: ${result.error || 'Unknown error'}`);
        }
      } else {
        const result = await addToFavorites(userEmail, recipe.id);
        if (!result.success) {
          // Revert on error
          setFavorites(prev => prev.filter(fav => fav.id !== recipe.id));
          console.error('Failed to add favorite:', result.error);
          alert(`Couldn't add to favorites: ${result.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert on error
      setFavorites(prev => 
        isFav
          ? [...prev, recipe]
          : prev.filter(fav => fav.id !== recipe.id)
      );
      alert(`Couldn't update favorites: ${error?.message || 'Unknown error'}`);
    }
  };

  const viewRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeModal(true);
  };

  const generateShoppingList = () => {
    const consolidatedList = {};
    
    mealPlan.forEach(recipe => {
      recipe.ingredients.filter(ing => ing.name && ing.name.trim()).forEach(ingredient => {
        const key = ingredient.name.toLowerCase().trim();
        if (consolidatedList[key]) {
          consolidatedList[key].amounts.push(ingredient.amount || '');
        } else {
          consolidatedList[key] = {
            item: ingredient.name.trim(),
            amounts: [ingredient.amount || ''],
            category: ingredient.category || 'Other'
          };
        }
      });
    });

    return Object.values(consolidatedList).sort((a, b) => 
      (a.category || 'Other').localeCompare(b.category || 'Other')
    );
  };

  const toggleItemCheck = (itemKey) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  // Cook Mode - Keep screen awake (works on both Android and iOS)
  const toggleCookMode = async () => {
    if (!cookMode) {
      // Enable Cook Mode
      let success = false;
      
      // Try Wake Lock API first (Android Chrome, newer browsers)
      try {
        if ('wakeLock' in navigator) {
          const lock = await navigator.wakeLock.request('screen');
          setWakeLock(lock);
          success = true;
          console.log('Cook Mode enabled - Wake Lock API');
        }
      } catch (err) {
        console.log('Wake Lock not available, trying video fallback');
      }
      
      // Fallback for iOS Safari - use silent video trick
      if (!success) {
        try {
          // Create a tiny silent video element
          const video = document.createElement('video');
          video.setAttribute('playsinline', '');
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.style.position = 'fixed';
          video.style.opacity = '0';
          video.style.pointerEvents = 'none';
          video.style.width = '1px';
          video.style.height = '1px';
          
          // Use a 1-second silent video (base64 encoded)
          video.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAu1tZGF0AAACrQYF//+p3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1NSByMjkwMSA3ZDBmZjIyIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxOCAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEgc2NlbmVjdXQ9NDAgaW50cmFfcmVmcmVzaD0wIHJjX2xvb2thaGVhZD00MCByYz1jcmYgbWJ0cmVlPTEgY3JmPTIzLjAgcWNvbXA9MC42MCBxcG1pbj0wIHFwbWF4PTY5IHFwc3RlcD00IGlwX3JhdGlvPTEuNDAgYXE9MToxLjAwAIAAAAAwZYiEAD//8m+P5OXfBeLGOf0Hg8K+AJhgAAADAAADAAADAAADAAAHgvugkks=';
          
          document.body.appendChild(video);
          await video.play();
          setNoSleepVideo(video);
          console.log('Cook Mode enabled - iOS video fallback');
        } catch (err) {
          console.error('Failed to enable iOS fallback:', err);
        }
      }
      
      setCookMode(true);
    } else {
      // Disable Cook Mode
      if (wakeLock) {
        wakeLock.release();
        setWakeLock(null);
      }
      if (noSleepVideo) {
        noSleepVideo.pause();
        noSleepVideo.remove();
        setNoSleepVideo(null);
      }
      setCookMode(false);
      console.log('Cook Mode disabled');
    }
  };

  // Cleanup wake lock and video when modal closes
  useEffect(() => {
    if (!showRecipeModal) {
      if (wakeLock) {
        wakeLock.release();
        setWakeLock(null);
      }
      if (noSleepVideo) {
        noSleepVideo.pause();
        noSleepVideo.remove();
        setNoSleepVideo(null);
      }
      if (cookMode) {
        setCookMode(false);
      }
    }
  }, [showRecipeModal, wakeLock, noSleepVideo, cookMode]);

  const printShoppingList = () => {
    const shoppingList = generateShoppingList();
    const printContent = `
      <html>
        <head>
          <title>Alera Shopping List</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #4a5568; border-bottom: 2px solid #68d391; padding-bottom: 10px; }
            .category { margin: 20px 0; }
            .category-title { font-weight: bold; color: #2d3748; margin-bottom: 10px; }
            .item { margin: 5px 0; padding: 5px; border-left: 3px solid #68d391; }
            .meal-section { margin-bottom: 30px; }
            .meal-title { font-weight: bold; color: #4a5568; }
          </style>
        </head>
        <body>
          <h1>🛒 Alera Shopping List</h1>
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
          
          <div class="meal-section">
            <h2>Selected Recipes:</h2>
            ${mealPlan.map(recipe => `
              <div class="meal-title">• ${recipe.title} (${recipe.servings} servings)</div>
            `).join('')}
          </div>

          <h2>Shopping List by Category:</h2>
          ${Object.entries(
            shoppingList.reduce((acc, item) => {
              if (!acc[item.category]) acc[item.category] = [];
              acc[item.category].push(item);
              return acc;
            }, {})
          ).map(([category, items]) => `
            <div class="category">
              <div class="category-title">${category}</div>
              ${items.map(item => `
                <div class="item">☐ ${item.item} - ${item.amounts.join(', ')}</div>
              `).join('')}
            </div>
          `).join('')}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const isInMealPlan = (recipeId) => mealPlan.find(item => item.id === recipeId);
  const isFavorite = (recipeId) => favorites.find(item => item.id === recipeId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20 px-3 sm:px-6 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <RouterLink
              to="/dashboard"
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </RouterLink>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-charcoal-800 mb-2">Member Recipes</h1>
            </div>
          </div>
          <div className="flex gap-2 sm:gap-3">
            {/* My Meal Plan Button */}
            <button
              onClick={() => setShowMealPlanModal(true)}
              style={{ backgroundColor: '#a8b5a0' }}
              className="relative text-white px-4 sm:px-6 py-3 rounded-xl hover:opacity-90 transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="hidden sm:inline">My Meal Plan</span>
              <span className="sm:hidden">Plan</span>
              {mealPlan.length > 0 && (
                <span className="bg-white/30 text-white text-xs rounded-full px-2 py-0.5 font-semibold">
                  {mealPlan.length}
                </span>
              )}
            </button>
          </div>
        </div>


        {/* Search Bar & Filters - Single Row */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-8">
          {/* Search Bar */}
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border-2 border-sage-200 rounded-lg px-3 py-2 pl-10 text-sm font-medium text-charcoal-700 placeholder-charcoal-400 hover:border-sage-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all shadow-sm"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="w-4 h-4 text-charcoal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-charcoal-400 hover:text-charcoal-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Meal Type Dropdown */}
          <div className="relative w-full sm:w-48">
            <select
              value={selectedMealType}
              onChange={(e) => setSelectedMealType(e.target.value)}
              onTouchStart={(e) => e.currentTarget.focus()}
              className="appearance-none bg-white border-2 border-sage-200 rounded-lg px-3 py-3 pr-10 text-sm font-medium text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors w-full shadow-sm cursor-pointer"
              style={{ WebkitAppearance: 'none', touchAction: 'manipulation' }}
            >
              {mealTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.icon} {type.name} ({type.count})
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Dietary Multi-Select Filter */}
          <div className="relative w-full sm:w-auto">
            <button
              onClick={() => setShowDietaryFilters(!showDietaryFilters)}
              className="bg-white border-2 border-sage-200 rounded-lg px-3 py-3 pr-10 text-sm font-medium text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors w-full sm:w-56 shadow-sm text-left flex items-center justify-between"
            >
              <span>
                {selectedDietary.length === 0 ? (
                  'Filter by Allergies'
                ) : selectedDietary.length === 1 ? (
                  dietaryFilters.find(f => f.id === selectedDietary[0])?.name || 'Allergies'
                ) : (
                  `${selectedDietary.length} Allergies Selected`
                )}
              </span>
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {showDietaryFilters && (
              <>
                {/* Backdrop to close dropdown when clicking outside */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowDietaryFilters(false)}
                />
                
                <div className="absolute top-full left-0 mt-2 w-full sm:w-80 bg-white border-2 border-sage-200 rounded-lg shadow-xl z-20 max-h-96 overflow-y-auto">
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-sage-100">
                      <span className="text-xs font-semibold text-charcoal-600 uppercase">Select Allergies</span>
                      {selectedDietary.length > 0 && (
                        <button
                          onClick={() => setSelectedDietary([])}  
                          className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    {dietaryFilters.map((filter) => {
                      const isSelected = selectedDietary.includes(filter.id);
                      return (
                        <label
                          key={filter.id}
                          className="flex items-center gap-3 py-2 px-2 hover:bg-sage-50 rounded-lg cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                setSelectedDietary(selectedDietary.filter(id => id !== filter.id));
                              } else {
                                setSelectedDietary([...selectedDietary, filter.id]);
                              }
                            }}
                            className="w-4 h-4 rounded border-sage-300 text-teal-600 focus:ring-teal-500 focus:ring-2 cursor-pointer"
                          />
                          <span className="flex-1 text-sm text-charcoal-700 font-medium">
                            {filter.name}
                          </span>
                          <span className="text-xs text-charcoal-500 font-medium bg-sage-50 px-2 py-1 rounded">
                            {filter.count}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Clear Filters Button */}
          {(selectedMealType !== 'all' || selectedDietary.length > 0) && (
            <button
              onClick={() => {
                setSelectedMealType('all');
                setSelectedDietary([]);
              }}
              className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium text-charcoal-600 hover:text-charcoal-800 border-2 border-sage-200 rounded-xl hover:bg-sage-50 transition-all shadow-md whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Recipe Grid */}
        {loadingRecipes && (
          <div className="text-center py-6 text-sm text-charcoal-600">Loading recipes…</div>
        )}
        {filteredRecipes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredRecipes.map((recipe, index) => (
              <motion.div
                key={recipe.id}
                className="bg-white rounded-2xl shadow-md border border-sage-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.05, 0.3) }}
              >
                {/* Recipe Image */}
                <div className="h-36 flex items-center justify-center text-4xl overflow-hidden relative group" style={{ background: 'linear-gradient(to bottom right, #e8ebe7, #d4ddd0, #c8d4c0)' }}>
                  {typeof recipe.image === 'string' && (
                    recipe.image.startsWith('http') || recipe.image.startsWith('/') || recipe.image.startsWith('data:')
                  ) ? (
                    <img 
                      src={recipe.image} 
                      alt={recipe.title} 
                      className="w-full h-full object-cover" 
                      loading="lazy"
                    />
                  ) : (
                    <span>{recipe.image || '🍽️'}</span>
                  )}
                </div>

                <div className="p-3 sm:p-4">
                  {/* Recipe Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <h3 className="text-sm font-bold text-charcoal-800 leading-tight">{recipe.title}</h3>
                    </div>
                    <button
                      onClick={() => toggleFavorite(recipe)}
                      className={`p-1 rounded-full transition-colors ${
                        isFavorite(recipe.id)
                          ? 'bg-pink-100 text-pink-600'
                          : 'bg-gray-100 text-gray-400 hover:bg-pink-100 hover:text-pink-600'
                      }`}
                    >
                      <span className="text-xs">❤️</span>
                    </button>
                  </div>

                  {/* Recipe Meta */}
                  <div className="flex flex-wrap gap-2 text-xs text-charcoal-600 mb-2">
                    <span>⏱️ {recipe.prepTime}</span>
                    <span>👥 {recipe.servings}</span>
                    {recipe.calories && <span className="font-semibold text-teal-600">🔥 {recipe.calories} cal</span>}
                  </div>


                  {/* Dietary Tags - Limited */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {recipe.dietary.slice(0, 2).map((tag, i) => (
                      <span key={i} className="text-xs bg-sage-100 text-sage-700 px-2 py-1 rounded-lg font-medium">
                        {tag.replace('-', ' ')}
                      </span>
                    ))}
                    {recipe.dietary.length > 2 && (
                      <span className="text-xs text-charcoal-500 font-medium">+{recipe.dietary.length - 2}</span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleMealPlan(recipe)}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                        isInMealPlan(recipe.id)
                          ? 'bg-sage-200 text-sage-800 hover:bg-sage-300'
                          : 'text-white shadow-sm hover:shadow-md hover:opacity-90'
                      }`}
                      style={!isInMealPlan(recipe.id) ? { backgroundColor: '#a8b5a0' } : {}}
                    >
                      {isInMealPlan(recipe.id) ? 'Remove' : '+ Plan'}
                    </button>
                    <button 
                      onClick={() => viewRecipe(recipe)}
                      className="px-3 py-2 bg-charcoal-700 text-white rounded-lg text-xs font-semibold hover:bg-charcoal-800 transition-all shadow-sm hover:shadow-md"
                    >
                      View
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-charcoal-800 mb-2">No recipes found</h3>
            <p className="text-charcoal-600">Try adjusting your filters to see more recipes</p>
          </div>
        )}

        {/* Meal Plan Modal */}
        {showMealPlanModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-4 sm:p-6 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg sm:text-xl font-bold text-charcoal-800">My Meal Plan ({mealPlan.length})</h3>
                  <button
                    onClick={() => setShowMealPlanModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-4 sm:p-6 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                {mealPlan.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {mealPlan.map((recipe) => (
                      <div 
                        key={recipe.id}
                        className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-lg transition-all"
                      >
                        <div 
                          className="h-32 overflow-hidden bg-sage-50 flex items-center justify-center cursor-pointer"
                          onClick={() => {
                            setSelectedRecipe(recipe);
                            setShowRecipeModal(true);
                          }}
                        >
                          {typeof recipe.image === 'string' && (recipe.image.startsWith('http') || recipe.image.startsWith('/')) ? (
                            <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-4xl">{recipe.image || '🍽️'}</span>
                          )}
                        </div>
                        <div className="p-3">
                          <h4 
                            className="font-semibold text-sm text-charcoal-800 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600"
                            onClick={() => {
                              setSelectedRecipe(recipe);
                              setShowRecipeModal(true);
                            }}
                          >
                            {recipe.title}
                          </h4>
                          <div className="flex items-center justify-between text-xs text-charcoal-600 mb-2">
                            <span>🍽️ {recipe.servings}</span>
                            <span>⏱️ {recipe.prepTime}</span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedRecipe(recipe);
                                setShowRecipeModal(true);
                              }}
                              className="flex-1 bg-blue-50 text-blue-600 py-1.5 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                            >
                              View Recipe
                            </button>
                            <button
                              onClick={() => removeFromMealPlanLocal(recipe.id)}
                              className="px-3 bg-red-50 text-red-600 py-1.5 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium"
                              title="Remove from meal plan"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">📋</div>
                    <p className="text-charcoal-600">Your meal plan is empty</p>
                  </div>
                )}
              </div>

              {mealPlan.length > 0 && (
                <div className="p-4 sm:p-6 border-t bg-gray-50 flex-shrink-0">
                  <button
                    onClick={() => {
                      setShowMealPlanModal(false);
                      setShowShoppingList(true);
                    }}
                    style={{ backgroundColor: '#a8b5a0' }}
                    className="w-full text-white py-3 rounded-lg hover:opacity-90 transition-all font-medium"
                  >
                    Generate Shopping List ({mealPlan.length} recipes)
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Shopping List Modal */}
        {showShoppingList && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-charcoal-800">Shopping List</h3>
                  <button
                    onClick={() => setShowShoppingList(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                {generateShoppingList().length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(
                      generateShoppingList().reduce((acc, item) => {
                        if (!acc[item.category]) acc[item.category] = [];
                        acc[item.category].push(item);
                        return acc;
                      }, {})
                    ).map(([category, items]) => (
                      <div key={category}>
                        <h4 className="font-semibold text-charcoal-800 mb-3 text-lg">{category}</h4>
                        <div className="space-y-2">
                          {items.map((item, index) => {
                            const itemKey = `${category}-${index}`;
                            return (
                              <div key={itemKey} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                                <input
                                  type="checkbox"
                                  checked={checkedItems[itemKey] || false}
                                  onChange={() => toggleItemCheck(itemKey)}
                                  style={{ accentColor: '#a8b5a0' }}
                                  className="w-4 h-4"
                                />
                                <span className={`flex-1 ${checkedItems[itemKey] ? 'line-through text-gray-500' : ''}`}>
                                  {item.item} - {item.amounts.join(', ')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🛒</div>
                    <p className="text-charcoal-600">No items in shopping list</p>
                  </div>
                )}
              </div>

              {mealPlan.length > 0 && (
                <div className="p-6 border-t bg-gray-50 flex gap-3 flex-shrink-0">
                  <button
                    onClick={printShoppingList}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    🖨️ Print Shopping List
                  </button>
                  <button
                    onClick={() => setShowShoppingList(false)}
                    className="px-6 bg-gray-200 text-charcoal-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recipe View Modal */}
        {showRecipeModal && selectedRecipe && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
              {/* Modal Header */}
              <div className="p-4 sm:p-6 border-b" style={{ background: 'linear-gradient(to right, #e8ebe7, #d4ddd0)' }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center text-xl sm:text-2xl flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #e8ebe7, #c8d4c0)' }}>
                      {typeof selectedRecipe.image === 'string' && (
                        selectedRecipe.image.startsWith('http') || selectedRecipe.image.startsWith('/') || selectedRecipe.image.startsWith('data:')
                      ) ? (
                        <img src={selectedRecipe.image} alt={selectedRecipe.title} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <span>{selectedRecipe.image || '🍽️'}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg sm:text-2xl font-bold text-slate-800 truncate">{selectedRecipe.title}</h2>
                      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 mt-1 flex-wrap">
                        <span>⏱️ {selectedRecipe.prepTime}</span>
                        <span>👥 {selectedRecipe.servings}</span>
                        {selectedRecipe.calories && <span className="font-semibold text-teal-600">🔥 {selectedRecipe.calories}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Cook Mode Toggle */}
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={toggleCookMode}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                          cookMode ? 'bg-gradient-to-r from-green-400 to-green-500 shadow-md' : 'bg-gray-200'
                        }`}
                        aria-label="Toggle Cook Mode"
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${
                            cookMode ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span className={`text-xs font-medium transition-colors hidden sm:inline ${
                        cookMode ? 'text-green-700' : 'text-gray-500'
                      }`}>
                        {cookMode ? 'Cooking!' : 'Cook Mode'}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowRecipeModal(false)}
                      className="text-slate-500 hover:text-slate-700 p-2 hover:bg-white hover:bg-opacity-50 rounded-full transition-colors"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Recipe Info */}
                  <div>
                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex text-yellow-400">
                        {'★'.repeat(Math.floor(selectedRecipe.rating))}
                        {'☆'.repeat(5 - Math.floor(selectedRecipe.rating))}
                      </div>
                      <span className="text-sm text-slate-600 font-medium">
                        {selectedRecipe.rating} ({selectedRecipe.reviews} reviews)
                      </span>
                    </div>

                    {/* Tags */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-800 mb-3">Dietary Information</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedRecipe.dietary.map((tag, i) => (
                          <span key={i} className="text-sm px-3 py-1 rounded-full font-medium" style={{ backgroundColor: '#e8ebe7', color: '#a8b5a0' }}>
                            {tag.replace('-', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Nutrition Information */}
                    {(selectedRecipe.calories || selectedRecipe.protein || selectedRecipe.carbs || selectedRecipe.fats) && (
                      <div className="mb-6 bg-gradient-to-br from-sage-50 to-beige-50 p-4 rounded-xl border border-sage-200">
                        <h3 className="text-lg font-semibold text-slate-800 mb-3">Nutrition Per Serving</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {selectedRecipe.calories && (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-sage-700">{selectedRecipe.calories}</div>
                              <div className="text-xs text-slate-600">Calories</div>
                            </div>
                          )}
                          {selectedRecipe.protein && (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-sage-700">{selectedRecipe.protein}g</div>
                              <div className="text-xs text-slate-600">Protein</div>
                            </div>
                          )}
                          {selectedRecipe.carbs && (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-sage-700">{selectedRecipe.carbs}g</div>
                              <div className="text-xs text-slate-600">Carbs</div>
                            </div>
                          )}
                          {selectedRecipe.fats && (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-sage-700">{selectedRecipe.fats}g</div>
                              <div className="text-xs text-slate-600">Fats</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Nutrition Highlights */}
                    {selectedRecipe.nutritionHighlights && selectedRecipe.nutritionHighlights.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-slate-800 mb-3">Nutrition Highlights</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedRecipe.nutritionHighlights.map((highlight, i) => (
                            <span key={i} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ingredients */}
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-3">Ingredients</h3>
                      <div className="space-y-2">
                        {selectedRecipe.ingredients.filter(ing => ing.name && ing.name.trim()).map((ingredient, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#a8b5a0' }}></div>
                            <span className="text-slate-700">
                              <span className="font-medium">{ingredient.amount?.trim() || ''}</span>
                              {ingredient.amount && ' '}
                              <span>{ingredient.name?.trim() || ''}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Instructions */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-3">Instructions</h3>
                    <div className="space-y-4">
                      {selectedRecipe.instructions.map((step, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 text-white rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: '#a8b5a0' }}>
                            {i + 1}
                          </div>
                          <p className="text-slate-700 leading-relaxed pt-1">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t bg-slate-50 flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <button
                  onClick={() => {
                    toggleMealPlan(selectedRecipe);
                  }}
                  className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                    isInMealPlan(selectedRecipe.id)
                      ? 'bg-sage-200 text-sage-800 hover:bg-sage-300'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={!isInMealPlan(selectedRecipe.id) ? { backgroundColor: '#a8b5a0' } : {}}
                >
                  {isInMealPlan(selectedRecipe.id) ? '✓ Remove from Plan' : '+ Add to Meal Plan'}
                </button>
                <button
                  onClick={() => toggleFavorite(selectedRecipe)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    isFavorite(selectedRecipe.id)
                      ? 'bg-rose-100 text-rose-700 border border-rose-300'
                      : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {isFavorite(selectedRecipe.id) ? '❤️ Favorited' : '🤍 Add to Favorites'}
                </button>
                <button
                  onClick={() => setShowRecipeModal(false)}
                  className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Alera Welcome Modal (first time after signup) */}
        <WelcomeModal
          isOpen={showAleraWelcome}
          onClose={closeAleraWelcome}
          title="Alera"
          icon="🌱"
        >
          <div className="space-y-3">
            <p className="text-charcoal-600 text-sm leading-relaxed">
              Thank you for being part of our wellness community. Your commitment to healing inspires us, and we're honored to support your journey.
            </p>
            <p className="text-charcoal-500 text-sm leading-relaxed">
              This is your personal wellness command center with everything you need: allergy-friendly recipes, gentle workout programs, supportive community, goal tracking, and curated resources. Every tool is crafted with love to help you thrive - explore at your own pace and remember, you're exactly where you need to be. ✨
            </p>
          </div>
        </WelcomeModal>

        {/* Page-Specific Welcome Modal */}
        <WelcomeModal
          isOpen={showWelcome}
          onClose={closeWelcome}
          title="Recipe Library"
          icon="🍽️"
        >
          <div className="space-y-4">
            <p>
              <strong>Welcome to your Recipe Collection!</strong> Here's how to make the most of it:
            </p>
            <ul className="space-y-2 text-left">
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">•</span>
                <span><strong>Browse recipes</strong> using the filters for meal type and dietary needs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">•</span>
                <span><strong>Add to Plan</strong> to build your weekly meal plan</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">•</span>
                <span><strong>Click the heart</strong> to save recipes to your favorites</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">•</span>
                <span><strong>View "My Meal Plan"</strong> to see your selections and generate a shopping list</span>
              </li>
            </ul>
            <p className="text-sm text-charcoal-600 italic">
              All recipes are allergy-friendly and include calorie information to support your wellness goals.
            </p>
          </div>
        </WelcomeModal>
      </div>
    </div>
  );
};

export default MemberRecipes;
